import React, { useEffect, useState } from "react";
import {
  Box,
  Divider,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Chip,
  TableContainer,
  Pagination,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { observer } from "mobx-react-lite";
import { useStore } from "../../app/stores/store";
import { useNavigate } from "react-router-dom";
import { Order } from "../../app/stores/orderStore";
import { cancelOrder } from "../../app/services/OrderService";
import { toast } from "react-toastify";

const ORDERS_PER_PAGE = 10;

const OrderPage: React.FC = observer(() => {
  const { orderStore } = useStore();
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedOrderUniqueId, setSelectedOrderUniqueId] = useState<string | null>(null);

  useEffect(() => {
    orderStore.loadOrders();
  }, [orderStore]);

  const getStatusStyle = (status: string) => {
    switch (status.toUpperCase()) {
      case "ORDERED":
      case "ORDER PLACED":
        return {
          backgroundColor: "#fff3e0",
          color: "#ef6c00",
        };
      case "ORDER PROCESSING":
        return {
          backgroundColor: "#b1d4f1ff",
          color: "#1565c0",
        };
      case "DELIVERED":
        return {
          backgroundColor: "#81e37cff",
          color: "#439347ff",
        };
      default:
        return {
          backgroundColor: "#eeeeee",
          color: "#424242",
        };
    }
  };

  const handleOpenDialog = (uniqueId: string) => {
    setSelectedOrderUniqueId(uniqueId);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedOrderUniqueId(null);
  };

  const handleConfirmCancel = async () => {
    if (!selectedOrderUniqueId) return;

    try {
      await cancelOrder(selectedOrderUniqueId);
      toast.success("Order cancelled successfully!");
      await orderStore.loadOrders();
    } catch (err) {
      toast.error("Failed to cancel order");
    } finally {
      handleCloseDialog();
    }
  };

  const totalOrders = orderStore.orders.length;
  const totalPages = Math.ceil(totalOrders / ORDERS_PER_PAGE);

  /*const paginatedOrders = [...orderStore.orders]
    .reverse()
    .slice((page - 1) * ORDERS_PER_PAGE, page * ORDERS_PER_PAGE);*/

  const paginatedOrders = [...orderStore.orders]
  .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()) // Sort by date DESC
  .slice((page - 1) * ORDERS_PER_PAGE, page * ORDERS_PER_PAGE);

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  return (
    <Box p={3}>
      <Typography fontSize={24} fontWeight={600} gutterBottom>
        All Orders
      </Typography>

      <Divider sx={{ borderColor: "lightgray", borderBottomWidth: 1, mb: 2 }} />

      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
            <TableRow>
              <TableCell>S.No.</TableCell>
              <TableCell>Order Id</TableCell>
              <TableCell>Ordered On</TableCell>
              <TableCell>Total Items</TableCell>
              <TableCell>Outlet</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedOrders.map((order: Order, index: number) => {
              const orderedDate = new Date(order.created_at);
              const status = order.statusHistory?.[0]?.status?.name ?? "N/A";
              const outlet = order.outlet.name?? "-";
              const totalItems = order.items?.length ?? 0;
              const isOrderPlaced = status.toLowerCase() === "order placed";

              return (
                <TableRow
                  key={order.unique_id}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={(e) => {
                    if ((e.target as HTMLElement).closest("button")) return;
                    navigate(`/order/${order.unique_id}`);
                  }}
                >
                  <TableCell>{(page - 1) * ORDERS_PER_PAGE + index + 1}</TableCell>
                  <TableCell>{order.unique_id}</TableCell>
                  <TableCell>
                    {orderedDate.toLocaleDateString()}{" "}
                    {orderedDate.toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </TableCell>
                  <TableCell>{totalItems} items</TableCell>
                  <TableCell>{outlet}</TableCell>
                  <TableCell>
                    <Chip
                      label={status}
                      variant="outlined"
                      sx={{
                        ...getStatusStyle(status),
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1.5,
                        border: "none",
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      disabled={!isOrderPlaced}
                      onClick={() => handleOpenDialog(order.unique_id)}
                    >
                      Cancel Order
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            shape="rounded"
            size="medium"
          />
        </Box>
      )}

      {/* Cancel Confirmation Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>Confirm Cancellation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to cancel this order? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
          <Button onClick={handleConfirmCancel} color="error" variant="contained">
            Cancel Order
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

export default OrderPage;
